/**
 * Payment Setup Hook
 * 
 * Manages Stripe payment setup for jobs, including payment method creation and status tracking.
 * This hook handles the client-side payment setup flow via Stripe Checkout Sessions.
 * 
 * @module usePaymentSetup
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

/**
 * Validates if a string is in valid UUID v4 format
 * 
 * @param str - The string to validate
 * @returns True if the string is a valid UUID, false otherwise
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Hook for managing payment setup for a specific job
 * 
 * This hook provides functionality to:
 * - Fetch and track payment setup status for both client and vendor
 * - Initiate Stripe Checkout Session for payment method setup
 * - Handle redirects to Stripe and back to the application
 * 
 * The payment setup flow:
 * 1. Client initiates payment setup via createPaymentSetup()
 * 2. Edge function creates Stripe Checkout Session
 * 3. User is redirected to Stripe to add payment method
 * 4. On success, user is redirected back to success URL
 * 5. Payment status is updated in database via webhook
 * 
 * @param jobId - The UUID of the job to setup payment for
 * @returns Object containing payment status, loading state, refetch function, and createPaymentSetup mutation
 * 
 * @example
 * ```tsx
 * function PaymentSetupPage({ jobId }) {
 *   const { paymentStatus, isLoading, createPaymentSetup } = usePaymentSetup(jobId);
 *   
 *   const handleSetup = () => {
 *     createPaymentSetup.mutate({
 *       successUrl: `${window.location.origin}/jobs/${jobId}/payment-complete`,
 *       cancelUrl: `${window.location.origin}/jobs/${jobId}/payment-setup`
 *     });
 *   };
 *   
 *   return (
 *     <div>
 *       {paymentStatus?.client_payment_complete ? (
 *         <p>Payment method already configured</p>
 *       ) : (
 *         <button onClick={handleSetup}>Setup Payment</button>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePaymentSetup = (jobId: string) => {
  const { toast } = useToast();

  // Fetch current user's organization ID
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile-org'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: paymentStatus, isLoading, refetch } = useQuery({
    queryKey: ['payment-setup', jobId],
    queryFn: async () => {
      if (!isValidUUID(jobId)) {
        console.error('Invalid UUID passed to usePaymentSetup:', jobId);
        return null;
      }

      const { data, error } = await supabase
        .from('payment_setup_status')
        .select('*')
        .eq('job_id', jobId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!jobId && isValidUUID(jobId),
  });

  const createPaymentSetup = useMutation({
    mutationFn: async ({ successUrl, cancelUrl }: { 
      successUrl: string; 
      cancelUrl: string; 
    }) => {
      if (!userProfile?.organization_id) {
        throw new Error('Organization ID is required');
      }

      console.log('Creating payment setup with:', { 
        jobId, 
        organizationId: userProfile.organization_id,
        successUrl,
        cancelUrl 
      });

      const { data, error } = await invokeEdgeFunction('create-payment-setup', {
        body: {
          jobId,
          organizationId: userProfile.organization_id,
          successUrl,
          cancelUrl,
        },
        callerOperation: 'usePaymentSetup.createPaymentSetup',
      });

      if (error) {
        console.error('Payment setup error:', error);
        throw error;
      }
      
      return data;
    },
    onSuccess: (response) => {
      console.log('Payment setup success - full response:', response);
      console.log('Response structure:', Object.keys(response || {}));
      
      // Handle both direct and nested response structures
      const result = response as { url?: string; data?: { url?: string } };
      const url = result?.url || result?.data?.url;
      
      console.log('Extracted URL:', url);
      
      if (url) {
        console.log('Redirecting to Stripe:', url);
        window.location.href = url;
      } else {
        console.error('No URL found in response. Full data:', JSON.stringify(response, null, 2));
        toast({
          title: 'Setup Failed',
          description: 'Could not initialize payment setup. Please refresh the page and try again.',
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      console.error('Error setting up payment:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      toast({
        title: 'Payment Setup Failed',
        description: `${errorMessage}. Please try again or contact support if the issue persists.`,
        variant: 'destructive',
      });
    },
  });

  return {
    paymentStatus,
    isLoading,
    refetch,
    createPaymentSetup,
  };
};
